import { Head, Link } from "@inertiajs/react"
import { Metaballs } from "@paper-design/shaders-react"
import {
  AlertTriangle,
  ArrowRight,
  Bot,
  ChevronLeft,
  ChevronRight,
  CircleGauge,
  Database,
  Link2,
  Network,
  Play,
  Quote,
  Sparkles,
  UserRoundCheck,
} from "lucide-react"
import { useEffect, useLayoutEffect, useRef, useState } from "react"
import { GuidedProductPreview } from "@/components/marketing/guided-product-preview"
import { IntegrationBeamSection } from "@/components/marketing/integration-beam-section"
import { MarketingLayout } from "@/components/marketing/marketing-layout"
import { ProductFeatureShowcase } from "@/components/marketing/product-feature-showcase"
import { SectionReveal } from "@/components/marketing/section-reveal"
import { useAppearance } from "@/hooks/use-appearance"
import { useMarketingLocale } from "@/hooks/use-marketing-locale"
import { productEntry } from "@/lib/product-entry"

const problems = [
  {
    title: "Reports start incomplete",
    body: "Developers lose time asking for account IDs, timestamps, and the exact failure state.",
    icon: UserRoundCheck,
  },
  {
    title: "Evidence lives in separate tools",
    body: "Support teams cannot safely connect a Slack report to records, logs, billing, and history.",
    icon: AlertTriangle,
  },
  {
    title: "Recurring issues stay repetitive",
    body: "The same investigation interrupts engineering because confirmed knowledge is not reusable.",
    icon: Link2,
  },
  {
    title: "Shortcuts create hidden risk",
    body: "A quick production change without authority, evidence, or verification is not a resolution.",
    icon: Bot,
  },
] as const

const pillars = [
  {
    name: "Intake",
    body: "Turn a plain-language Slack or web report into a structured case and ask only what is missing.",
    outcome: "The investigation starts with one clear owner and scope.",
    icon: Network,
    visual: ["Report", "Clarify", "Assign"],
  },
  {
    name: "Investigate",
    body: "Read approved records, events, knowledge, and previous incidents through bounded connectors.",
    outcome: "Every finding opens to the evidence that supports it.",
    icon: Database,
    visual: ["Query", "Compare", "Explain"],
  },
  {
    name: "Govern",
    body: "Preview allowlisted actions, show authority, and require the right person to approve execution.",
    outcome: "No operational change is hidden behind an AI suggestion.",
    icon: Sparkles,
    visual: ["Preview", "Approve", "Verify"],
  },
  {
    name: "Learn",
    body: "Turn developer-confirmed investigations into versioned playbooks and complete escalations.",
    outcome: "Recurring support work becomes safer and more reusable.",
    icon: CircleGauge,
    visual: ["Confirm", "Version", "Reuse"],
  },
] as const

const proofScenarios = [
  {
    label: "Fictional demo · Billing entitlement",
    title: "Payment succeeded, but the account still has starter access.",
    body: "Resolto compares approved billing and application records, exposes the failed sync, and prepares a bounded entitlement action for approval.",
    outcome: "Evidence: Stripe fixture, account record, sync event, approval",
  },
  {
    label: "Fictional demo · Authentication",
    title: "A customer cannot sign in after changing their email.",
    body: "The case links recent login evidence to the approved identity record and explains the mismatch without exposing secrets.",
    outcome: "Evidence: identity record, login events, verified outcome",
  },
  {
    label: "Fictional demo · Scheduled reports",
    title: "A monthly report stopped because required source data is missing.",
    body: "Resolto separates the confirmed missing field from uncertain downstream impact and asks for the one clarification still required.",
    outcome: "Evidence: job event, contract record, missing-data state",
  },
  {
    label: "Fictional demo · Workflow state",
    title: "A completed onboarding flow is stuck at the final transition.",
    body: "The timeline connects document approvals, the transition timeout, ownership, and the safe escalation path.",
    outcome: "Evidence: workflow record, timeout event, escalation package",
  },
  {
    label: "Fictional demo · Code proposal",
    title: "A recurring defect becomes a bounded proposal, never an autonomous deployment.",
    body: "Resolto scopes the affected files and tests for developer review while merge and deployment remain outside its authority.",
    outcome: "Evidence: confirmed incident, patch scope, checks, review boundary",
  },
] as const

const walkthroughVideos = [
  {
    label: "Investigation walkthrough",
    title: "Diagnose a billing entitlement mismatch",
    body: "Follow intake, approved reads, evidence comparison, likely cause, and the next safe action.",
    tab: "Investigate",
  },
  {
    label: "Approval walkthrough",
    title: "Review a bounded operational action",
    body: "See the exact proposed mutation, required authority, expected effect, and verification step before execution.",
    tab: "Approve",
  },
  {
    label: "Escalation walkthrough",
    title: "Send engineering a complete technical case",
    body: "Package the timeline, evidence, uncertainty, reproduction context, and attempted actions without another clarification round.",
    tab: "Escalate",
  },
] as const

const heroBrands = [
  "Slack intake",
  "Structured cases",
  "Evidence timelines",
  "Human approvals",
  "Audit history",
  "Versioned playbooks",
] as const

const finalCtaFlowColors = {
  light: ["#1297fd", "#8bcaf9", "#fcfcfc", "#e3e4e6", "#40aaf8", "#8bcaf9", "#fcfcfc", "#c3c3c5"],
  dark: ["#1297fd", "#092b47", "#061d31", "#174769", "#40aaf8", "#092b47", "#061d31", "#174769"],
} as const

function SectionKicker({ children }: { children: string }) {
  return <span className="section-kicker">{children}</span>
}

function FinalCtaBackdrop() {
  const [reduceMotion, setReduceMotion] = useState(false)
  const { resolvedAppearance } = useAppearance()
  const isDark = resolvedAppearance === "dark"

  useEffect(() => {
    const query = window.matchMedia("(prefers-reduced-motion: reduce)")
    const updatePreference = () => setReduceMotion(query.matches)

    updatePreference()
    query.addEventListener("change", updatePreference)

    return () => query.removeEventListener("change", updatePreference)
  }, [])

  return (
    <span className="home-final-cta__flow" aria-hidden="true">
      <Metaballs
        className="home-final-cta__shader"
        colorBack={isDark ? "#061d31" : "#fcfcfc"}
        colors={[...finalCtaFlowColors[isDark ? "dark" : "light"]]}
        count={20}
        size={0.58}
        speed={reduceMotion ? 0 : 0.48}
        frame={14400}
        scale={1.05}
        rotation={-7}
        offsetX={0.02}
        offsetY={0.04}
        fit="cover"
        minPixelRatio={1}
        maxPixelCount={1_000_000}
      />
    </span>
  )
}

function HeroProductStage() {
  const stageRef = useRef<HTMLElement>(null)
  const entranceRef = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    const stage = stageRef.current
    const entrance = entranceRef.current

    if (!stage) {
      return
    }

    const motionPreference = window.matchMedia("(prefers-reduced-motion: reduce)")
    let frame = 0
    let paintFrame = 0
    let playFrame = 0

    if (!motionPreference.matches && entrance) {
      entrance.dataset.ready = "true"
      paintFrame = window.requestAnimationFrame(() => {
        playFrame = window.requestAnimationFrame(() => {
          entrance.dataset.running = "true"
        })
      })
    }

    const update = () => {
      frame = 0

      if (motionPreference.matches) {
        stage.style.setProperty("--preview-progress", "1")

        return
      }

      const { top } = stage.getBoundingClientRect()
      const start = window.innerHeight * 0.96
      const finish = window.innerHeight * 0.28
      const progress = Math.min(1, Math.max(0, (start - top) / (start - finish)))

      stage.style.setProperty("--preview-progress", progress.toFixed(3))
    }

    const requestUpdate = () => {
      if (!frame) {
        frame = window.requestAnimationFrame(update)
      }
    }

    update()
    window.addEventListener("scroll", requestUpdate, { passive: true })
    window.addEventListener("resize", requestUpdate)
    motionPreference.addEventListener("change", requestUpdate)

    return () => {
      window.removeEventListener("scroll", requestUpdate)
      window.removeEventListener("resize", requestUpdate)
      motionPreference.removeEventListener("change", requestUpdate)
      entrance?.removeAttribute("data-ready")
      entrance?.removeAttribute("data-running")
      window.cancelAnimationFrame(paintFrame)

      if (frame) {
        window.cancelAnimationFrame(frame)
      }

      if (playFrame) {
        window.cancelAnimationFrame(playFrame)
      }
    }
  }, [])

  return (
    <section ref={stageRef} className="home-product-stage">
      <div className="home-product-stage__haze" aria-hidden="true" />
      <div className="home-product-preview marketing-shell">
        <div ref={entranceRef} className="home-product-preview__entrance">
          <SectionKicker>Live product sequence</SectionKicker>
          <GuidedProductPreview />
        </div>
      </div>
    </section>
  )
}

export default function Welcome() {
  const { t } = useMarketingLocale()

  return (
    <MarketingLayout>
      <Head>
        <meta name="description" content={t("hero.body")} />
      </Head>

      <section className="home-hero marketing-shell">
        <div className="home-hero__copy">
          <h1>
            {t("hero.title")
              .split("|")
              .map((line) => (
                <span key={line}>{line}</span>
              ))}
          </h1>
          <p className="home-hero__body">{t("hero.body")}</p>
          <div className="home-hero__actions">
            <Link href={productEntry} className="marketing-primary-button">
              {t("hero.primary")}
            </Link>
            <Link href="/contact" className="marketing-secondary-button">
              {t("hero.secondary")}
              <ArrowRight aria-hidden="true" />
            </Link>
          </div>
        </div>
        <div className="sample-brand-strip" aria-hidden="true">
          <div className="sample-brand-strip__viewport">
            <div className="sample-brand-strip__track">
              {(["primary", "duplicate"] as const).flatMap((cycle) =>
                heroBrands.map((brand) => <span key={`${cycle}-${brand}`}>{brand}</span>),
              )}
            </div>
          </div>
        </div>
      </section>

      <HeroProductStage />

      <section className="problem-section">
        <SectionReveal className="marketing-shell problem-section__inner">
          <div className="section-copy problem-section__heading">
            <SectionKicker>Why internal support breaks down</SectionKicker>
            <h2>When context is missing, engineering becomes the queue.</h2>
            <p>
              Vague reports, scattered evidence, and unsafe shortcuts turn recurring issues into
              developer interruptions.
            </p>
          </div>
          <div className="problem-section__list">
            {problems.map(({ title, body, icon: Icon }, index) => (
              <article key={title}>
                <span>0{index + 1}</span>
                <Icon aria-hidden="true" />
                <div>
                  <h3>{title}</h3>
                  <p>{body}</p>
                </div>
              </article>
            ))}
          </div>
        </SectionReveal>
      </section>

      <SectionReveal className="pillar-section marketing-shell">
        <div className="section-copy section-copy--wide">
          <SectionKicker>How Resolto restores control</SectionKicker>
          <h2>From unclear report to verified next step.</h2>
          <p>
            Resolto makes evidence, uncertainty, authority, and verification part of the same case.
          </p>
        </div>
        <div className="pillar-section__grid">
          {pillars.map(({ name, body, outcome, icon: Icon, visual }, index) => (
            <article key={name}>
              <header>
                <span>0{index + 1}</span>
                <Icon aria-hidden="true" />
              </header>
              <h3>{name}</h3>
              <p>{body}</p>
              <div className="pillar-section__visual">
                {visual.map((step, stepIndex) => (
                  <span key={step}>
                    {stepIndex + 1}
                    <strong>{step}</strong>
                  </span>
                ))}
              </div>
              <small>{outcome}</small>
            </article>
          ))}
        </div>
      </SectionReveal>

      <ProductFeatureShowcase />
      <IntegrationBeamSection />

      <ProductWalkthrough />
      <ProductProof />

      <section className="home-final-cta">
        <div className="marketing-shell home-final-cta__inner">
          <FinalCtaBackdrop />
          <div>
            <SectionKicker>Start with Resolto</SectionKicker>
            <h2>Resolve the next recurring issue without another developer interruption.</h2>
            <p>
              Explore a persistent fictional-data workspace, or book a walkthrough around one real
              support workflow.
            </p>
          </div>
          <div>
            <Link href={productEntry} className="marketing-primary-button">
              <span>Open demo</span>
            </Link>
            <Link href="/contact" className="marketing-secondary-button">
              Book a demo
              <ArrowRight aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>
    </MarketingLayout>
  )
}

function ProductWalkthrough() {
  const [{ active, direction }, setSelection] = useState<{
    active: number
    direction: "forward" | "backward"
  }>({ active: 0, direction: "forward" })
  const current = walkthroughVideos[active] ?? walkthroughVideos[0]

  if (!current) return null

  const show = (index: number, requestedDirection?: "forward" | "backward") => {
    setSelection((currentSelection) => {
      const next = (index + walkthroughVideos.length) % walkthroughVideos.length

      if (next === currentSelection.active) {
        return currentSelection
      }

      return {
        active: next,
        direction: requestedDirection ?? (next > currentSelection.active ? "forward" : "backward"),
      }
    })
  }

  return (
    <section id="product-walkthrough" className="walkthrough-launch marketing-shell">
      <SectionReveal className="walkthrough-launch__inner">
        <div className="walkthrough-launch__intro">
          <SectionKicker>Product walkthroughs</SectionKicker>
          <h2>See the control points in a real resolution flow.</h2>
          <p>
            Explore the fictional demo from investigation through approval and engineering
            escalation.
          </p>
        </div>

        <section
          className="walkthrough-carousel"
          aria-roledescription="carousel"
          aria-label="Resolto product walkthroughs"
        >
          <div className="walkthrough-carousel__video">
            <div key={current.label} className="walkthrough-carousel__consent">
              <Play aria-hidden="true" />
              <strong>{current.title}</strong>
              <p>
                Intake → approved evidence → explained finding → governed next step → verification
              </p>
              <Link href="/app/cases/case_payment_entitlement">Open sample case</Link>
            </div>
            <span>Functional Resolto demo · Fictional workspace and data</span>
          </div>

          <div className="walkthrough-carousel__caption">
            <div className="walkthrough-carousel__caption-copy" aria-live="polite">
              {walkthroughVideos.map((video, index) => (
                <div
                  key={video.label}
                  className="walkthrough-carousel__caption-panel"
                  data-active={active === index}
                  aria-hidden={active !== index}
                >
                  <span>{video.label}</span>
                  <h3>{video.title}</h3>
                  <p>{video.body}</p>
                </div>
              ))}
            </div>
            <div className="walkthrough-carousel__arrows">
              <button
                type="button"
                aria-label="Previous walkthrough video"
                onClick={() => show(active - 1, "backward")}
              >
                <ChevronLeft aria-hidden="true" />
              </button>
              <button
                type="button"
                aria-label="Next walkthrough video"
                onClick={() => show(active + 1, "forward")}
              >
                <ChevronRight aria-hidden="true" />
              </button>
            </div>
          </div>

          <fieldset
            className="walkthrough-carousel__tabs"
            aria-label="Choose walkthrough"
            data-direction={direction}
          >
            {walkthroughVideos.map((video, index) => (
              <button
                key={video.label}
                type="button"
                aria-current={active === index}
                onClick={() => show(index)}
              >
                <span>0{index + 1}</span>
                {video.tab}
              </button>
            ))}
          </fieldset>
        </section>
      </SectionReveal>
    </section>
  )
}

function ProductProof() {
  const duration = 6500
  const [active, setActive] = useState(0)
  const [reduceMotion, setReduceMotion] = useState(false)

  useEffect(() => {
    const query = window.matchMedia("(prefers-reduced-motion: reduce)")
    const updatePreference = () => {
      setReduceMotion(query.matches)
    }

    updatePreference()
    query.addEventListener("change", updatePreference)

    return () => query.removeEventListener("change", updatePreference)
  }, [])

  useEffect(() => {
    if (reduceMotion) {
      return
    }

    const timer = window.setInterval(() => {
      setActive((current) => (current + 1) % proofScenarios.length)
    }, duration)

    return () => window.clearInterval(timer)
  }, [reduceMotion])

  const select = (index: number) => {
    setActive(index)
  }

  return (
    <SectionReveal className="testimonial-section">
      <div className="marketing-shell testimonial-section__inner">
        <div className="testimonial-section__heading">
          <div>
            <SectionKicker>Product proof</SectionKicker>
            <h2>See every decision behind the resolution.</h2>
          </div>
          <p>
            Follow the operating events, controls and accountable decisions behind each workflow.
          </p>
        </div>

        <section
          className="testimonial-carousel"
          data-assistant-tone="dark"
          aria-roledescription="carousel"
          aria-label="Product workflows"
        >
          <div className="testimonial-carousel__viewport">
            <div
              className="testimonial-carousel__track"
              style={{
                transform: `translate3d(-${active * 100}%, 0, 0)`,
              }}
            >
              {proofScenarios.map((scenario, index) => (
                <article
                  key={scenario.title}
                  className="testimonial-card"
                  aria-hidden={active !== index}
                  aria-label={`${index + 1} of ${proofScenarios.length}`}
                >
                  <Quote aria-hidden="true" />
                  <div className="testimonial-card__copy">
                    <span>{scenario.label}</span>
                    <h3>{scenario.title}</h3>
                    <p>{scenario.body}</p>
                    <Link
                      href="/app/cases/case_payment_entitlement"
                      tabIndex={active === index ? 0 : -1}
                    >
                      Open sample case
                      <ArrowRight aria-hidden="true" />
                    </Link>
                  </div>
                  <div className="testimonial-card__meta">
                    <strong>{scenario.outcome}</strong>
                    <span>Product workflow · Operating sequence</span>
                  </div>
                </article>
              ))}
            </div>
          </div>

          <div className="testimonial-carousel__controls">
            <div className="testimonial-carousel__dots">
              {proofScenarios.map((scenario, index) => (
                <button
                  key={scenario.title}
                  type="button"
                  aria-label={`Show product workflow ${index + 1}`}
                  aria-current={active === index}
                  onClick={() => select(index)}
                >
                  {active === index && !reduceMotion && (
                    <span
                      key={active}
                      style={{
                        animationDuration: `${duration}ms`,
                      }}
                    />
                  )}
                </button>
              ))}
            </div>
            <div>
              <button
                type="button"
                aria-label="Previous product workflow"
                onClick={() => select((active - 1 + proofScenarios.length) % proofScenarios.length)}
              >
                <ChevronLeft aria-hidden="true" />
              </button>
              <button
                type="button"
                aria-label="Next product workflow"
                onClick={() => select((active + 1) % proofScenarios.length)}
              >
                <ChevronRight aria-hidden="true" />
              </button>
            </div>
          </div>
        </section>
      </div>
    </SectionReveal>
  )
}
